import {useCallback, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import axios from 'axios'
import type {CancelTokenSource} from 'axios'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import OrderService from '../core/_requests'
import {
  getCarrierService,
  isTimeoutError,
  normalizeCheapestRates,
  normalizeRateText,
  RATE_REQUEST_TIMEOUT,
} from '../components/order-child/rateHelpers'

type MergeCallback = (options: any[], chooseRate: any) => void

interface UseCarrierRateFetcherOptions {
  /** Called after each carrier response is merged. Receives the full merged
   *  options array so the consumer can derive chooseRate / update state. */
  onMerge: MergeCallback
  /** Called when a carrier request fails (non-cancel). */
  onError?: (response: any) => void
  /**
   * If true, the requestId guard is enabled. Stale responses from previous
   * fetch cycles will be discarded. Consumers must call `nextRequestId()`
   * before starting a new fetch cycle.
   */
  useRequestIdGuard?: boolean
}

/**
 * Encapsulates the per-carrier rate fetching engine:
 * - cancel-token management
 * - progressive merge via normalizeCheapestRates
 * - error-rate generation for failed carriers
 * - loadingCarriers tracking
 * - pendingCarriers tracking for choose-rate deferral
 * - cleanup on unmount
 */
export const useCarrierRateFetcher = ({
  onMerge,
  onError,
  useRequestIdGuard = false,
}: UseCarrierRateFetcherOptions) => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  const {isCancel} = useCancelToken()
  const carriers = statesGlobal.carriers

  const [loadingCarriers, setLoadingCarriers] = useState<string[]>([])
  const [retryingCarriers, setRetryingCarriers] = useState<string[]>([])
  const rateCancelSources = useRef<Set<CancelTokenSource>>(new Set())
  const dataRatesRef = useRef<any>({chooseRate: null, options: []})
  const requestIdRef = useRef<number>(0)
  const pendingCarriersRef = useRef<string[]>([])

  /** Increment the request ID to invalidate in-flight responses from prior cycles. */
  const nextRequestId = useCallback(() => {
    requestIdRef.current += 1
    pendingCarriersRef.current = []
    return requestIdRef.current
  }, [])

  /** Cancel all in-flight carrier requests. */
  const cancelAll = useCallback(() => {
    rateCancelSources.current.forEach((s) => s.cancel())
    rateCancelSources.current.clear()
    pendingCarriersRef.current = []
    setLoadingCarriers([])
    setRetryingCarriers([])
  }, [])

  /** Reset internal accumulated options (call before a fresh fetch cycle). */
  const reset = useCallback(() => {
    const initial = {chooseRate: null, options: []}
    dataRatesRef.current = initial
    return initial
  }, [])

  const buildCarrierErrorRate = useCallback(
    (carrierName: string, error: any) => {
      const carrierService = getCarrierService(carriers, carrierName)
      const timeout = isTimeoutError(error)
      const errorMessage = error?.response?.data?.message || error?.message
      return {
        carrier_service: `${carrierName}_error`,
        display_name: carrierService?.visuals?.display_name || carrierName,
        logo: carrierService?.visuals?.logo,
        error: normalizeRateText(errorMessage, intl.formatMessage({id: 'ERROR'})),
        isTimeout: timeout,
        carrierName,
      }
    },
    [carriers, intl]
  )

  const mergeRateOptions = useCallback(
    (newRates: any[], guardId?: number) => {
      if (useRequestIdGuard && guardId !== undefined && guardId !== requestIdRef.current) return

      const currentOptions = Array.isArray(dataRatesRef.current?.options)
        ? dataRatesRef.current.options
        : []
      const nextRates = Array.isArray(newRates) ? newRates : []
      const options = normalizeCheapestRates([...currentOptions, ...nextRates])
      dataRatesRef.current = {...dataRatesRef.current, options}
      onMerge(options, dataRatesRef.current.chooseRate)
    },
    [onMerge, useRequestIdGuard]
  )

  /** Fetch rates for a single carrier. Manages its own cancel token. */
  const fetchOne = useCallback(
    async (payload: any, guardId?: number) => {
      const carrierName = payload?.shipment?.carrier
      const source = axios.CancelToken.source()
      rateCancelSources.current.add(source)

      if (carrierName) {
        setLoadingCarriers((prev) => (prev.includes(carrierName) ? prev : [...prev, carrierName]))
      }

      try {
        const {rates} = await OrderService.rates(payload, {
          cancelToken: source.token,
          timeout: RATE_REQUEST_TIMEOUT,
        })
        if (carrierName) {
          pendingCarriersRef.current = pendingCarriersRef.current.filter((c) => c !== carrierName)
        }
        mergeRateOptions(rates, guardId)
      } catch (error: any) {
        if (isCancel(error)) return
        if (carrierName) {
          pendingCarriersRef.current = pendingCarriersRef.current.filter((c) => c !== carrierName)
          mergeRateOptions([buildCarrierErrorRate(carrierName, error)], guardId)
        }
        onError?.(error?.response)
      } finally {
        rateCancelSources.current.delete(source)
        if (carrierName) {
          setLoadingCarriers((prev) => prev.filter((c) => c !== carrierName))
        }
      }
    },
    [mergeRateOptions, buildCarrierErrorRate, isCancel, onError]
  )

  /**
   * Fetch rates for multiple carriers in parallel.
   * @param payloads - array of rate payloads (one per carrier)
   * @param priorityCarrier - optional carrier name to fetch first (awaited before the rest)
   */
  const fetchAll = useCallback(
    async (payloads: any[], priorityCarrier?: string | null) => {
      const guardId = requestIdRef.current
      const carrierNamesToLoad = payloads
        .map((p) => p?.shipment?.carrier)
        .filter(Boolean) as string[]
      pendingCarriersRef.current = carrierNamesToLoad
      setLoadingCarriers(carrierNamesToLoad)

      if (priorityCarrier) {
        const priorityPayload = payloads.find((p) => p?.shipment?.carrier === priorityCarrier)
        const rest = payloads.filter((p) => p?.shipment?.carrier !== priorityCarrier)
        if (priorityPayload) {
          await fetchOne(priorityPayload, guardId)
        }
        await Promise.allSettled(rest.map((p) => fetchOne(p, guardId)))
      } else {
        await Promise.allSettled(payloads.map((p) => fetchOne(p, guardId)))
      }
    },
    [fetchOne]
  )

  /** Update the internal chooseRate (used by consumers after applyChooseRate). */
  const setChooseRate = useCallback((chooseRate: any) => {
    dataRatesRef.current = {...dataRatesRef.current, chooseRate}
  }, [])

  /**
   * Retry rates for a carrier, keeping the error card visible during the retry
   * (shows loading spinner on the existing card). On success the error entry is
   * removed and replaced with real rates. On failure the error entry is replaced.
   */
  const retryOneKeepVisible = useCallback(
    async (payload: any) => {
      const carrierName = payload?.shipment?.carrier
      const source = axios.CancelToken.source()
      rateCancelSources.current.add(source)

      if (carrierName) {
        setLoadingCarriers((prev) => (prev.includes(carrierName) ? prev : [...prev, carrierName]))
        setRetryingCarriers((prev) => (prev.includes(carrierName) ? prev : [...prev, carrierName]))
      }

      try {
        const {rates} = await OrderService.rates(payload, {
          cancelToken: source.token,
          timeout: RATE_REQUEST_TIMEOUT,
        })

        if (carrierName) {
          const currentOptions = Array.isArray(dataRatesRef.current?.options)
            ? dataRatesRef.current.options
            : []
          const filtered = currentOptions.filter(
            (item: any) => item?.carrier_service !== `${carrierName}_error`
          )
          const nextRates = Array.isArray(rates) ? rates : []
          const options = normalizeCheapestRates([...filtered, ...nextRates])
          dataRatesRef.current = {...dataRatesRef.current, options}
          onMerge(options, dataRatesRef.current.chooseRate)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        if (carrierName) {
          const currentOptions = Array.isArray(dataRatesRef.current?.options)
            ? dataRatesRef.current.options
            : []
          const filtered = currentOptions.filter(
            (item: any) => item?.carrier_service !== `${carrierName}_error`
          )
          const errorRate = buildCarrierErrorRate(carrierName, error)
          const options = normalizeCheapestRates([...filtered, errorRate])
          dataRatesRef.current = {...dataRatesRef.current, options}
          onMerge(options, dataRatesRef.current.chooseRate)
        }
        if (!isTimeoutError(error)) {
          onError?.(error?.response)
        }
      } finally {
        rateCancelSources.current.delete(source)
        if (carrierName) {
          setLoadingCarriers((prev) => prev.filter((c) => c !== carrierName))
          setRetryingCarriers((prev) => prev.filter((c) => c !== carrierName))
        }
      }
    },
    [buildCarrierErrorRate, isCancel, onError, onMerge]
  )

  return {
    loadingCarriers,
    retryingCarriers,
    fetchOne,
    fetchAll,
    retryOneKeepVisible,
    cancelAll,
    reset,
    setChooseRate,
    dataRatesRef,
    nextRequestId,
    pendingCarriersRef,
    requestIdRef,
  }
}
